import { cn } from '@eliadmin/core/src/utils/style-utils';
import { CreateCurriculumSection } from '../create-curriculum-section';
import { TranslateFn, useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useFormContext } from 'react-hook-form';
import { Field } from '../../auth/field';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries, fetchGenders } from '@eliadmin/core/src/api/general/general-api';
import { Separator } from '@eliadmin/core/src/components/ui/separator';
import { CreateCurriculumSubheader } from '../create-curriculum-subheader';
import { fetchJobCategories } from '@eliadmin/core/src/api/jobportal/categories-api';
import { FaLinkedin, FaFacebook, FaInstagram, FaTwitter, FaGithub } from 'react-icons/fa';
import { getSocialByName } from '../create-curriculum-socials-utils';
import { fetchEmploymentTypes } from '@eliadmin/core/src/api/jobportal/create-curriculum-api';

export const CreateCurriculumStep0 = () => {
  const { t } = useTranslations();
  const form = useFormContext();
  const locale = useLocaleStore(state => state.currentLocale);

  const socials = form.watch('socials');

  const { data: genders = { data: [] }, isLoading: isGendersLoading } = useQuery({
    queryKey: [CacheKey.GENDERS, locale],
    queryFn: () => fetchGenders({ locale }),
    refetchOnWindowFocus: false,
  });

  const { data: countries = { data: [] }, isLoading: isCountriesLoading } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () =>
      fetchCountries({ locale }).then(res => {
        if (res.data.length === 1) {
          form.setValue('country', res.data[0].id);
        }
        return res;
      }),
    refetchOnWindowFocus: false,
  });

  const { data: jobCategories = { data: [] }, isLoading: isJobCategoriesLoading } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_CATEGORIES, locale],
    queryFn: () =>
      fetchJobCategories({ locale, query: { results: 150 } }).then(res =>
        res.data.map(o => {
          return { label: o.name, value: o.id };
        }),
      ),
    refetchOnWindowFocus: false,
  });

  const { data: employmentTypes } = useQuery({
    queryKey: [CacheKey.JOBPORTAL_EMPLOYMENT_TYPES],
    queryFn: () =>
      fetchEmploymentTypes({ locale }).then(r => r.data.map(g => ({ label: g.name, value: g.id }))),
  });

  return (
    <div className={cn('create-curriculum-step-wrapper flex w-full flex-col gap-4')}>
      <CreateCurriculumSection title={t('text861')}>
        <Field
          control={form.control}
          name="gender.id"
          type="radio"
          isDisabled={isGendersLoading}
          options={genders.data.map(g => ({
            value: g.id,
            label: g.name,
          }))}
          placeholder={t('text252')}
        />
        <Field
          name="name"
          label={t('text33')}
          placeholder={t('text33') + '...'}
          required
          control={form.control}
        />
        <Field
          name="surname"
          label={t('text34')}
          placeholder={t('text34') + '...'}
          required
          control={form.control}
        />
        <Field
          name="position_name"
          label={t('text1604')}
          placeholder={t('text1604') + '...'}
          control={form.control}
        />
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <Field
            name="title_before"
            label={t('text1726')}
            placeholder={t('text1726') + '...'}
            control={form.control}
          />
          <Field
            name="title_after"
            label={t('text1755')}
            placeholder={t('text1755') + '...'}
            control={form.control}
          />
        </div>
        <Separator className="bg-[#D8D8DB]" />
        <CreateCurriculumSubheader title={t('text389')} />
        <Field
          name="date_of_birth"
          placeholder={t('text389') + '...'}
          type="date"
          control={form.control}
        />
        <Separator className="bg-[#D8D8DB]" />
        <CreateCurriculumSubheader title={t('Pracovný pomer')} />
        <Field
          control={form.control}
          name="employment_types"
          type="multiselect"
          options={employmentTypes || []}
          placeholder="Vyberte vyhovujúce pracovné pomery..."
          className={{ wrapper: 'h-16' }}
        />
      </CreateCurriculumSection>
      <CreateCurriculumSection title={t('text360')}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <Field
            name="email"
            label={t('text35')}
            placeholder={t('text35') + '...'}
            control={form.control}
          />
          <Field
            name="phone"
            label={t('text36')}
            type="tel"
            placeholder={t('text36') + '...'}
            control={form.control}
          />
        </div>
      </CreateCurriculumSection>
      <CreateCurriculumSection title={t('text1597')}>
        {Object.keys(socials).map((key, index) => {
          const currentSocial = getSocialByName(key, t);

          return (
            <div key={index} className="grid grid-cols-1 gap-8 md:grid-cols-2">
              <Field
                name={`socials.${key}.name`}
                label={`${currentSocial?.label || 'ERROR: ADD TO SOCIALS MAPPING'} - Názov`}
                placeholder="Názov..."
                control={form.control}
              />
              <Field
                name={`socials.${key}.url`}
                label={`${currentSocial?.label || 'ERROR: ADD TO SOCIALS MAPPING'} - URL`}
                placeholder={currentSocial?.label + ' URL...'}
                control={form.control}
              />
            </div>
          );
        })}
      </CreateCurriculumSection>
      <CreateCurriculumSection title={t('text157')}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <Field
            control={form.control}
            name="address"
            label={t('text38')}
            placeholder={t('text38') + '...'}
          />
          <Field
            control={form.control}
            name="street_number"
            label={t('text485')}
            placeholder={t('text485') + '...'}
          />
          <Field
            control={form.control}
            name="city"
            label={t('text39')}
            placeholder={t('text39') + '...'}
          />
          <Field
            control={form.control}
            name="postcode"
            label={t('text40')}
            placeholder={t('text40') + '...'}
          />
        </div>
        <Field
          control={form.control}
          name="country"
          type="select"
          options={countries?.data.map(c => ({ value: c.id, label: c.name })) || []}
          label={t('text41')}
        />
      </CreateCurriculumSection>
      <CreateCurriculumSection title={t('text1743')}>
        <p className="text-[#63626F]">{t('text1756')}</p>
        <Field
          control={form.control}
          name="categories"
          type="multiselect"
          placeholder="Vyberte kategórie..."
          menuPlacement="top"
          options={(jobCategories as any) || []}
          label={t('text1743')}
        />
      </CreateCurriculumSection>
    </div>
  );
};
